import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
    ReportConfig,
    ReportProps,
    ReportWrapperProps,
    ReportWrapperRefAPI,
    TableReportHeader,
    TableReportRow,
} from 'c-reports/Types';
import { ReportDateTimeframe, ReportType } from 'c-reports/Types';
import { format, parseISO, isValid as isValidDate } from 'date-fns';
import { Box, Stack, Typography } from '@mui/material';
import { useDateUtils, useUrlState, useUrlStateValue } from 'c-hooks';
import { ReportWrapper } from 'c-reports/Components';
import type { DisplayMediaBreakdownData } from 'c-sdk';
import { useDebounce } from 'use-debounce';
import { Checkbox } from 'c-components';
import { useBoolean } from 'react-hanger';
import { useCommonTranslation } from 'c-translation';
import type { NetworkRequest } from 'c-data-layer';
import { NetworkRequestState } from 'c-data-layer';
import { parsedLogFileParsedMediaDataDates, parseLogFileParsedMediaData } from './lib';
import { groupByHourRows, groupByMediaByHourRows, groupByMediaRows } from './LogFileParsedRows';

type Props = {
    minDate: Date;
    maxDate: Date;
    chartName: string;
    fetchData: ReportConfig<{ data: DisplayMediaBreakdownData[] }>['fetchData'];

    showGroupByCheckboxes?: boolean;
    defaultGroupByMedia?: boolean;
    defaultGroupByHour?: boolean;

    startAtEnd?: boolean;
    firstAndLastSeen?: { firstSeen: string | null; lastSeen: string | null } | null;
    urlStatePath?: string;
};

const stackSx = { maxWidth: '100%', overflow: 'hidden', width: '100%' };

const LogFileParsedDaily: React.FC<Props> = ({
    minDate,
    maxDate,
    chartName,
    fetchData,

    showGroupByCheckboxes = true,
    defaultGroupByMedia = true,
    defaultGroupByHour = false,

    startAtEnd = true,
    firstAndLastSeen = null,
    urlStatePath,
}) => {
    const [data, setData] = useState<DisplayMediaBreakdownData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pending, setPending] = useState(false);

    const urlEnabled = urlStatePath != null;
    const { pushMany } = useUrlState();
    const urlGroupByMedia = useUrlStateValue<boolean | null>(
        urlEnabled ? `${urlStatePath}.groupByMedia` : undefined,
        defaultGroupByMedia,
    );
    const urlGroupByHour = useUrlStateValue<boolean | null>(
        urlEnabled ? `${urlStatePath}.groupByHour` : undefined,
        defaultGroupByHour,
    );
    const urlTimeframe = useUrlStateValue<ReportDateTimeframe | string | null>(
        urlEnabled ? `${urlStatePath}.timeframe` : undefined,
        ReportDateTimeframe.Daily,
    );
    const resolvedTimeframe = useMemo(() => {
        if (urlTimeframe === null || urlTimeframe === undefined || urlTimeframe === '') {
            return ReportDateTimeframe.Daily;
        }
        const parsed = Number(urlTimeframe);
        return Number.isFinite(parsed) ? parsed : ReportDateTimeframe.Daily;
    }, [urlTimeframe]);
    const urlStartDate = useUrlStateValue<string | null>(
        urlEnabled ? `${urlStatePath}.startDate` : undefined,
        null,
    );
    const urlEndDate = useUrlStateValue<string | null>(
        urlEnabled ? `${urlStatePath}.endDate` : undefined,
        null,
    );

    const parsedUrlStartDate = useMemo(() => {
        if (!urlStartDate) return null;
        const parsed = parseISO(urlStartDate);
        return isValidDate(parsed) ? parsed : null;
    }, [urlStartDate]);

    const parsedUrlEndDate = useMemo(() => {
        if (!urlEndDate) return null;
        const parsed = parseISO(urlEndDate);
        return isValidDate(parsed) ? parsed : null;
    }, [urlEndDate]);

    const hourlyState = useBoolean(
        urlEnabled && typeof urlGroupByHour === 'boolean' ? urlGroupByHour : defaultGroupByHour,
    );
    const groupByMediaState = useBoolean(
        urlEnabled && typeof urlGroupByMedia === 'boolean' ? urlGroupByMedia : defaultGroupByMedia,
    );

    useEffect(() => {
        if (!urlEnabled) return;
        if (typeof urlGroupByMedia === 'boolean' && urlGroupByMedia !== groupByMediaState.value) {
            groupByMediaState.setValue(urlGroupByMedia);
        }
    }, [groupByMediaState, urlEnabled, urlGroupByMedia]);

    useEffect(() => {
        if (!urlEnabled) return;
        if (typeof urlGroupByHour === 'boolean' && urlGroupByHour !== hourlyState.value) {
            hourlyState.setValue(urlGroupByHour);
        }
    }, [hourlyState, urlEnabled, urlGroupByHour]);
    const [showMediaGrouped] = useDebounce(groupByMediaState.value, 500);
    const [showHourly] = useDebounce(hourlyState.value, 500);

    const groupByParam = useMemo<string[] | undefined>(() => {
        if (!showMediaGrouped) return ['datetime'];
        return showHourly ? ['media_id', 'datetime'] : ['media_id', 'date'];
    }, [showMediaGrouped, showHourly]);

    const [appliedGroupBy, setAppliedGroupBy] = useState<string[] | undefined>(groupByParam);

    const lastStableDataRef = useRef<DisplayMediaBreakdownData[]>([]);
    const lastHeadersRef = useRef<TableReportHeader[]>([]);
    const lastRowsRef = useRef<TableReportRow[]>([]);

    const onDataUpdated: ReportConfig<{ data: DisplayMediaBreakdownData[] }>['onDataUpdated'] =
        useCallback(
            resp => {
                const next = resp?.data ?? [];
                setData(next);
                lastStableDataRef.current = next;
                setAppliedGroupBy(groupByParam);
            },
            [groupByParam],
        );

    const onLoadingStateUpdated = useCallback((req?: NetworkRequest) => {
        const inProgress = req?.state === NetworkRequestState.InProgress;
        setIsLoading(inProgress);
        if (!inProgress) {
            setPending(false);
        }
    }, []);

    const fetchDataWithGrouping = useCallback(
        (start, end, timeframe) => fetchData(start, end, timeframe, groupByParam),
        [fetchData, groupByParam],
    );

    const reportWrapperProps = useMemo<ReportWrapperProps<any>>(
        () => ({
            fetchData: fetchDataWithGrouping,
            onDataUpdated,
            onLoadingStateUpdated,
            initialStartDate: parsedUrlStartDate ?? (startAtEnd ? maxDate : minDate),
            initialEndDate: parsedUrlEndDate ?? maxDate,
            reportChartWrapperProps: { height: 500 },
            dateSettings: {
                timeframe: urlEnabled ? resolvedTimeframe : ReportDateTimeframe.Daily,
                showGoStart: true,
                showGoEnd: true,
                minDate,
                maxDate,
                availableTimeframes: [
                    ReportDateTimeframe.Daily,
                    ReportDateTimeframe.Weekly,
                    ReportDateTimeframe.BiWeekly,
                    ReportDateTimeframe.Monthly,
                    ReportDateTimeframe.Range,
                ],
                onDatesUpdated: urlEnabled
                    ? (timeframe, start, end) => {
                          if (!start || !end) return;
                          pushMany({
                              [`${urlStatePath}.timeframe`]: timeframe,
                              [`${urlStatePath}.startDate`]: format(start, 'yyyy-MM-dd'),
                              [`${urlStatePath}.endDate`]: format(end, 'yyyy-MM-dd'),
                          });
                      }
                    : undefined,
            },
        }),
        [
            fetchDataWithGrouping,
            maxDate,
            minDate,
            onDataUpdated,
            onLoadingStateUpdated,
            parsedUrlEndDate,
            parsedUrlStartDate,
            pushMany,
            startAtEnd,
            urlEnabled,
            urlStatePath,
            resolvedTimeframe,
        ],
    );

    const reportWrapperRef = useRef<ReportWrapperRefAPI>(null);

    useEffect(() => {
        setPending(true);
        reportWrapperRef.current?.refreshData?.();
    }, [groupByParam]);

    const handleGroupByMediaChange = useCallback(
        (value: boolean) => {
            groupByMediaState.setValue(value);
            if (!urlEnabled) return;
            pushMany({
                [`${urlStatePath}.groupByMedia`]: value,
                [`${urlStatePath}.groupByHour`]: hourlyState.value,
            });
        },
        [groupByMediaState, hourlyState.value, pushMany, urlEnabled, urlStatePath],
    );

    const handleGroupByHourChange = useCallback(
        (value: boolean) => {
            hourlyState.setValue(value);
            if (!urlEnabled) return;
            pushMany({
                [`${urlStatePath}.groupByMedia`]: groupByMediaState.value,
                [`${urlStatePath}.groupByHour`]: value,
            });
        },
        [groupByMediaState.value, hourlyState, pushMany, urlEnabled, urlStatePath],
    );

    const block = isLoading || pending;

    const dataForRender = useMemo(() => (block ? lastStableDataRef.current : data), [block, data]);

    const transformedData = useMemo(
        () => parseLogFileParsedMediaData(dataForRender),
        [dataForRender],
    );

    const renderGroupByMedia = useMemo(
        () => Array.isArray(appliedGroupBy) && appliedGroupBy.includes('media_id'),
        [appliedGroupBy],
    );
    const renderGroupByHour = useMemo(
        () => Array.isArray(appliedGroupBy) && appliedGroupBy.includes('datetime'),
        [appliedGroupBy],
    );

    const allDatesInRange = useMemo(
        () => parsedLogFileParsedMediaDataDates(transformedData),
        [transformedData],
    );

    const { formatDateString, dayMonthYearApiFormat } = useDateUtils();

    const computedHeaders = useMemo<TableReportHeader[]>(
        () => [
            { content: '' },
            ...allDatesInRange.map(day => {
                let colSpan = 0;
                const dateKey = format(day, dayMonthYearApiFormat);
                if (renderGroupByMedia && renderGroupByHour) {
                    colSpan = Object.keys(transformedData.day?.[dateKey]?.hours ?? {}).length;
                }
                return {
                    tableCellProps: {
                        colSpan,
                        sx: { textAlign: 'left', position: 'sticky', left: 0 },
                    },
                    content: (
                        <Box fontSize=".8em" lineHeight="1.2em">
                            <Box sx={{ textTransform: 'uppercase' }}>
                                {formatDateString({ date: day, dateFormatString: 'EEE' })}
                            </Box>
                            <Box sx={{ whiteSpace: 'nowrap' }}>
                                {formatDateString({ date: day, dateFormatString: 'do MMM' })}
                            </Box>
                        </Box>
                    ),
                };
            }),
        ],
        [
            allDatesInRange,
            dayMonthYearApiFormat,
            formatDateString,
            renderGroupByHour,
            renderGroupByMedia,
            transformedData.day,
        ],
    );

    const computedRows = useMemo<TableReportRow[]>(() => {
        if (renderGroupByMedia && renderGroupByHour)
            return groupByMediaByHourRows(allDatesInRange, transformedData, dayMonthYearApiFormat);
        if (renderGroupByMedia)
            return groupByMediaRows(allDatesInRange, transformedData, dayMonthYearApiFormat);
        return groupByHourRows(allDatesInRange, transformedData, dayMonthYearApiFormat);
    }, [
        renderGroupByMedia,
        renderGroupByHour,
        allDatesInRange,
        transformedData,
        dayMonthYearApiFormat,
    ]);

    const tableHeaders = useMemo<TableReportHeader[]>(() => {
        if (block && lastHeadersRef.current.length) return lastHeadersRef.current;
        lastHeadersRef.current = computedHeaders;
        return computedHeaders;
    }, [computedHeaders, block]);

    const tableRows = useMemo<TableReportRow[]>(() => {
        if (block && lastRowsRef.current.length) return lastRowsRef.current;
        lastRowsRef.current = computedRows;
        return computedRows;
    }, [computedRows, block]);

    const reportProps = useMemo<ReportProps<unknown>[]>(
        () => [
            {
                type: ReportType.Table,
                namePrefix: chartName,
                data: dataForRender,
                rows: tableRows,
                headers: tableHeaders,
                disableVirtualization: true,
                dense: true,
            },
        ],
        [dataForRender, chartName, tableHeaders, tableRows],
    );

    const t = useCommonTranslation();

    return (
        <Stack gap={2} sx={stackSx}>
            {showGroupByCheckboxes && (
                <Stack direction="row" px={2} alignItems="center" gap={2}>
                    <Checkbox
                        isBoolean
                        value={groupByMediaState.value}
                        onChange={handleGroupByMediaChange}
                        label={t('Component.LogFileParsedDaily.groupByMediaCheckbox')}
                    />
                    {groupByMediaState.value && (
                        <Checkbox
                            isBoolean
                            value={hourlyState.value}
                            onChange={handleGroupByHourChange}
                            label={t('Component.LogFileParsedDaily.groupByHourCheckbox')}
                        />
                    )}
                    {firstAndLastSeen != null && (
                        <Typography pt={1}>
                            {firstAndLastSeen?.firstSeen ? firstAndLastSeen.firstSeen : ''}
                            {!firstAndLastSeen.firstSeen || !firstAndLastSeen.lastSeen ? '' : ' - '}
                            {firstAndLastSeen?.lastSeen ? firstAndLastSeen.lastSeen : ''}
                        </Typography>
                    )}
                </Stack>
            )}
            <Box height="75vh" overflow="hidden" display="flex" width="100%">
                <ReportWrapper
                    {...reportWrapperProps}
                    ref={reportWrapperRef}
                    reportProps={reportProps}
                    wrapperProps={{
                        sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            overflow: 'hidden',
                        },
                    }}
                    reportWrapperProps={{ sx: { flex: 1, overflow: 'auto' } }}
                />
            </Box>
        </Stack>
    );
};

export default LogFileParsedDaily;
